/**
 * @format
 * @module ui/login
 */
import extend from "lodash/extend";
import consts from "lib/consts";
import ee from "lib/eventemitter";
import { configureModal } from "ui/modal-manager";
import { isDesktop } from "utils/detect";
import logger from "utils/logger";
import { isFunctionThenInvoke } from "utils/utils";
import { setAfterLoginCookie, isLoggedIn } from "lib/identity/api";
import { setProp, cacheToken } from "utils/utils";

const debugTag = "[LOGIN]";

const defaultConfig = {
  uid: "login-modal",
  topPaddingForCloseButton: false,
  sourceElement: ".js-onclick--login",
  scrollable: true,
  iframeUrl: consts.URLS.MODALS.LOGIN,
  height: "600"
};

/**
 * @method showLoginModal
 * @description will create an instance of the login modal with default configurations
 */
export let showLoginModal = () => {};

/**
 * @method init
 * @description bootstrap login modal
 */
export function init() {
  if (isLoggedIn()) {
    return;
  }
  showLoginModal = configureModal(defaultConfig);
  setProp(window, "tgam.ui.login.show", uiLoginShow);
}

/**
 * @method uiLoginShow
 * @description Allow optional url override for modal iframe
 * @param {Object} [c={}] - collection of params
 * @param {String} [c.url] - for iframe src
 */
function uiLoginShow({ url } = {}) {
  // We need a random token, in case Darwin calls this method multiple times.
  let customConfig = { uid: `${defaultConfig.uid}-darwin-${cacheToken()}` };
  if (url) {
    customConfig.iframeUrl = url;
  }
  const darwinConfig = extend({}, defaultConfig, customConfig);
  const showDarwinModal = configureModal(darwinConfig);
  willShowLoginModal(showDarwinModal, darwinConfig.iframeUrl);
}

/**
 * @method willShowLoginModal
 * @description will either show a login modal or redirect you to the login page
 * @param {Function} callback - method to invoke to display modal
 * @param {String} [mobileUrl=consts.URLS.LOGIN] - for mobile redirection
 */
export function willShowLoginModal(callback, mobileUrl = consts.URLS.LOGIN) {
  if (isDesktop()) {
    logger.info(`${debugTag} DESKTOP detected, show modal`);
    isFunctionThenInvoke(callback);
  } else {
    logger.info(`${debugTag} NOT DESKTOP detect, redirect`);
    window.location.href = mobileUrl;
  }
}

/**
 * @method bootstrapProtectedLoginModal
 * @description creates a version of the register modal which will override the close function and not allow it to close
 * @param {Function} callback - for on close event
 * @returns {Function}
 */
export function bootstrapProtectedLoginModal(callback) {
  // extends default object with a different uid and onCloseFunction
  const protectedModalConfig = extend({}, defaultConfig, {
    uid: "login-protected",
    onCloseFunction: callback
  });
  return configureModal(protectedModalConfig);
}

/**
 * @method loginAnchorClick
 * @description event listener function for when the signin button is clicked
 * @param {MouseEvent} e - a mouse click event
 * @param {String} featureLabel - feature name to report
 */
export function loginAnchorClick(e, featureLabel = "") {
  logger.info(`${debugTag} loginAnchorClick()`, e);
  // if the optional attribute, then set after login cookie url for mobile only
  const after = e.target.dataset.afterLoginGoto;
  if (!isDesktop() && after) {
    setAfterLoginCookie(after);
  }
  // only show modal when in desktop view
  // if in tablet or mobile view, the anchor tag will follow the href value
  if (isDesktop()) {
    e.preventDefault();

    const a = e.target.closest("a");
    const url = a ? a.getAttribute("href").replace("user/", "user/modal/") : null;
    logger.info(`${debugTag} loginAnchorClick()`, e, a, url);

    showLoginModal(url);
  }
  // Don't trigger a second event if the clicked element already pushes data via data-analyticsclick
  if (!e.target.closest("[data-analyticsclick]")) {
    ee.trigger("analytics.trackGTMEvent", [
      {
        feature: featureLabel || "unknown",
        type: "link",
        label: "log in"
      }
    ]);
  }
  // propagation must be stopped here otherwise the document.click listener in data-collection
  // will be triggered resulting in:
  // 1. the analyticsLayer receiving two clickEvents
  // 2. the modal will not be shown -- the anchor href will be followed (because of #1)
  e.stopPropagation();
}
